import { useRef } from 'react';
import { useFirebaseDocuments } from './useFirebaseDocuments';

export const useFirebasePopulate = <T>() => {
  const cacheRef = useRef<Map<string, Promise<any>>>(new Map());
  const { getDocData } = useFirebaseDocuments();

  const fetchDoc = async (path: string): Promise<any> => {
    const cacheMap = cacheRef.current;
    if (cacheMap.has(path)) return cacheMap.get(path)!;

    const promise = getDocData(path);
    cacheMap.set(path, promise);

    return promise;
  };

  const resolvePath = async (parent: any, segments: string[], idx: number): Promise<any> => {
    if (idx >= segments.length || parent == null) return parent;
    const key = segments[idx];

    if (Array.isArray(parent)) {
      await Promise.all(parent.map((el: any) => resolvePath(el, segments, idx)));
      return parent;
    }

    if (parent[key] == null) return parent;
    const value = parent[key];

    if (Array.isArray(value)) {
      parent[key] = await Promise.all(
        value.map(async (el: any) => (el && typeof el === 'object' && el.path ? fetchDoc(el.path) : el))
      );

      await Promise.all(parent[key].map((el: any) => resolvePath(el, segments, idx + 1)));
    } else if (value && typeof value === 'object' && value.path) {
      const doc = await fetchDoc(value.path);
      parent[key] = doc;
      await resolvePath(doc, segments, idx + 1);
    } else {
      await resolvePath(value, segments, idx + 1);
    }

    return parent;
  };

  const populateItems = async (items: T[], paths: string[]): Promise<T[]> => {
    return Promise.all(
      items.map(async item => {
        const itemCopy = { ...item } as any;
        for (const path of paths) await resolvePath(itemCopy, path.split('.'), 0);

        return itemCopy as T;
      })
    );
  };

  const populateItem = async (item: T, paths: string[]): Promise<T> => {
    const itemCopy = { ...item } as any;
    for (const p of paths) await resolvePath(itemCopy, p.split('.'), 0);

    return itemCopy as T;
  };

  return { populateItems, populateItem, getDocData: fetchDoc };
}
